import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit
} from 'components/Filters'
import { Tag } from 'components/v2/Tag'
import { IPlacesFiltersForm } from 'features/Places/types'
import { STATES } from 'utils/states'
import { COUNTRY_CODES } from 'utils/countries'
import { SelectState } from 'features/Cities/components/SelectState'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { ICompanyAutocomplete } from 'features/Companies/components/MultiCompanyAutocomplete'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { Input } from 'components/Input'

const messages = defineMessages({
  codePlaceholder: {
    id: 'places.filters.codePlaceholder',
    defaultMessage: 'Enter code'
  },
  selectCompany: {
    id: 'places.filters.selectCompany',
    defaultMessage: 'Select company'
  },
  selectPlace: {
    id: 'places.filters.selectPlace',
    defaultMessage: 'Select place'
  },
  selectCity: {
    id: 'places.filters.selectCity',
    defaultMessage: 'Select city'
  },
  selectBusinessClient: {
    id: 'places.filters.selectBusinessClient',
    defaultMessage: 'Select business client'
  },
  selectBusinessSite: {
    id: 'places.filters.selectBusinessSite',
    defaultMessage: 'Select business site'
  }
})

const defaultValues: IPlacesFiltersForm = {
  code: '',
  company: {
    id: '',
    name: ''
  },
  place: {
    id: '',
    name: ''
  },
  city: {
    id: '',
    name: ''
  },
  state: {
    code: '',
    name: ''
  },
  businessClient: {
    id: '',
    name: ''
  },
  businessSite: {
    id: '',
    name: ''
  }
}

interface IPlacesFiltersProps {
  filters: IPlacesFiltersForm
  onSubmit: (values: IPlacesFiltersForm) => void
}

export const PlacesFilters = ({ filters, onSubmit }: IPlacesFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const initialValues: IPlacesFiltersForm = {
    code: filters.code || defaultValues.code,
    company: filters.company || defaultValues.company,
    place: filters.place || defaultValues.place,
    city: filters.city || defaultValues.city,
    state: filters.state || defaultValues.state,
    businessClient: filters.businessClient || defaultValues.businessClient,
    businessSite: filters.businessSite || defaultValues.businessSite
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleBlur, handleSubmit, handleChange, setFieldValue, setValues, values } = props

        const handleStateChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
          const state = STATES.find((state) => state.code === value) || defaultValues.state
          setFieldValue('state', {
            code: value,
            name: state.name
          })
          onSubmit({
            ...values,
            state
          })
        }

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'code':
                          label = <FormattedMessage id="places.list.code" defaultMessage="Code" />
                          value = filterValue as string
                          break
                        case 'company':
                          label = <FormattedMessage id="dictionary.company" defaultMessage="Company" />
                          value = (filterValue as IPlacesFiltersForm['company']).name
                          break
                        case 'place':
                          label = <FormattedMessage id="dictionary.place" defaultMessage="Place" />
                          value = (filterValue as IPlacesFiltersForm['place']).name
                          break
                        case 'city':
                          label = <FormattedMessage id="dictionary.city" defaultMessage="City" />
                          value = (filterValue as IPlacesFiltersForm['city']).name
                          break
                        case 'state':
                          label = <FormattedMessage id="dictionary.state" defaultMessage="State" />
                          value = (filterValue as IPlacesFiltersForm['state']).name
                          break
                        case 'businessClient':
                          label = <FormattedMessage id="places.list.client" defaultMessage="Client" />
                          value = (filterValue as IPlacesFiltersForm['businessClient']).name
                          break
                        case 'businessSite':
                          label = <FormattedMessage id="places.list.site" defaultMessage="Site" />
                          value = (filterValue as IPlacesFiltersForm['businessSite']).name
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Input
                    id="code"
                    value={values.code}
                    placeholder={intl.formatMessage(messages.codePlaceholder)}
                    onChange={handleChange}
                  />
                  <CompanyAutocomplete
                    id="company"
                    value={values.company?.name || ''}
                    placeholder={intl.formatMessage(messages.selectCompany)}
                    onChange={(company: ICompanyAutocomplete): void => {
                      const newValues = {
                        ...values,
                        company: {
                          id: company.id || '',
                          name: company.name || ''
                        },
                        place: defaultValues.place
                      }
                      setValues(newValues)
                      onSubmit(newValues)
                    }}
                  />
                  <PlaceAutocomplete
                    id="place"
                    value={values.place?.name || ''}
                    placeholder={intl.formatMessage(messages.selectPlace)}
                    onChange={(place): void => {
                      setFieldValue('place', place)
                      onSubmit({
                        ...values,
                        place
                      })
                    }}
                    companyId={values.company.id || undefined}
                  />
                  <CityAutocomplete
                    id="city"
                    value={values.city?.name || ''}
                    placeholder={intl.formatMessage(messages.selectCity)}
                    onChange={(city): void => {
                      setFieldValue('city', city)
                      onSubmit({
                        ...values,
                        city
                      })
                    }}
                  />
                  <SelectState
                    id="stateCode"
                    value={values.state?.code || ''}
                    onChange={handleStateChange}
                    onBlur={handleBlur}
                    country={COUNTRY_CODES.MEXICO}
                  />
                  <BusinessClientAutocomplete
                    id="businessClient"
                    value={values.businessClient?.name || ''}
                    placeholder={intl.formatMessage(messages.selectBusinessClient)}
                    onChange={(businessClient): void => {
                      setFieldValue('businessClient', businessClient)
                      onSubmit({
                        ...values,
                        businessClient
                      })
                    }}
                  />
                  <BusinessSiteAutocomplete
                    id="businessSite"
                    value={values.businessSite.name || ''}
                    placeholder={intl.formatMessage(messages.selectBusinessSite)}
                    onChange={(businessSite): void => {
                      setFieldValue('businessSite', businessSite)
                      onSubmit({
                        ...values,
                        businessSite
                      })
                    }}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
